import { ComponentProps, useState, useMemo, RefObject, useEffect } from 'react'
import { Option } from '@/components/common/Form/types'
import { isTimeline, areTimelinesEqual } from '@/utils/date'
import { Dropdown } from '../Dropdown/Dropdown'
import { FilterButton } from '../Filter/Filter/FilterButton'
import { RadioGroupProps, RadioGroup } from '../Form'

type RadioSelectProps = Omit<
    Pick<RadioGroupProps, 'options' | 'value' | 'renderLabel'>,
    'onChange'
> & {
    name: string
    label?: React.ReactNode
    description?: React.ReactNode
    isOpen: boolean
    onClick: () => void
    onChange: (value: string) => void
    dropdownRef?: React.RefObject<HTMLDivElement>
    wrapperClassname?: string
    buttonWrapperClassname?: string
    buttonClassname?: string
    dropdownClassname?: string
    itemWrapperClassname?: string
    radioClassName?: string
    defaultValue?: string
    noValueLabel?: React.ReactNode
    enableDefaultSelection?: boolean
    renderValueLabel?: (option: Option) => React.ReactNode
    matchReferenceWidth?: boolean
    buttonRef?: RefObject<HTMLButtonElement>
    dropdownOffset?: number
}

export const RadioSelect: React.FC<RadioSelectProps> = (props) => {
    const {
        name,
        label,
        options,
        isOpen,
        onClick,
        value,
        defaultValue,
        onChange,
        description,
        dropdownRef,
        renderLabel,
        renderValueLabel,
        noValueLabel,
        enableDefaultSelection,
        matchReferenceWidth,
        dropdownOffset,
    } = props

    const selectedOption = useMemo(() => {
        const selectedOption = options?.find((option) => {
            if (defaultValue && !value) {
                return defaultValue === option.value
            }

            if (isTimeline(value) && isTimeline(option.value)) {
                return areTimelinesEqual(value, option.value)
            }
            return value === option.value
        })
        if (selectedOption) {
            return selectedOption
        }
        return options?.[0]
    }, [options, value, defaultValue])

    useEffect(() => {
        if (!enableDefaultSelection || value) return

        if (selectedOption?.value) {
            onChange(selectedOption.value as string)
        }
    }, [enableDefaultSelection, onChange, selectedOption?.value, value])

    const valueLabel = useMemo(() => {
        if (noValueLabel) return noValueLabel

        if (renderValueLabel && selectedOption) {
            return renderValueLabel(selectedOption)
        }

        return selectedOption?.label
    }, [noValueLabel, renderValueLabel, selectedOption])

    return (
        <Dropdown
            name={`${name}-dropdown`}
            buttonComponent={FilterButton}
            buttonProps={{
                name: name,
                label: label,
                description: description,
                valueLabel,
                className: props.buttonClassname,
                buttonRef: props.buttonRef,
            }}
            onClick={onClick}
            isOpen={isOpen}
            wrapperClassname={props.wrapperClassname}
            buttonWrapperClassname={props.buttonWrapperClassname}
            dropdownClassname={props.dropdownClassname}
            dropdownRef={dropdownRef}
            matchReferenceWidth={matchReferenceWidth}
            dropdownOffset={dropdownOffset}
        >
            <div className="py-2">
                <RadioGroup
                    options={options}
                    value={value}
                    onChange={onChange as RadioGroupProps['onChange']}
                    name={`${name}-radio`}
                    itemWrapperClassname={`py-2 px-4 ${props.itemWrapperClassname || ''}`}
                    radioClassName={props.radioClassName || ''}
                    renderLabel={renderLabel}
                />
            </div>
        </Dropdown>
    )
}

type UncontrolledRadioSelectProps = {
    name: string
    options: ComponentProps<typeof RadioSelect>['options']
    value: string
}

export const UncontrolledRadioSelect: React.FC<UncontrolledRadioSelectProps> = (
    props
) => {
    const { name, options } = props
    const [value, setValue] = useState(props.value)
    const [isOpen, setIsOpen] = useState(false)

    return (
        <RadioSelect
            isOpen={isOpen}
            name={name}
            options={options}
            value={value}
            onChange={setValue}
            onClick={() => {
                setIsOpen((isOpen) => !isOpen)
            }}
        />
    )
}
